/**
 * Pure header-matching helper shared by the availability resolvers.
 *
 * Distinct from `computeColumnSignature` (`../column-signature.ts`), which
 * builds a stable lookup key for a whole header set. This helper answers a
 * narrower question: "does this file have a column shaped like X?", doing a
 * case/whitespace-insensitive comparison against a target header name.
 *
 * Reuses `normalizeHeader` from `../column-signature.ts` (identical
 * trim/lowercase/whitespace-collapse normalization) instead of redefining
 * it here — re-exported as `normalizeHeaderForMatch` to keep this module's
 * public name stable for existing importers.
 *
 * No DB, R2, or Redis — pure logic only.
 */
import { normalizeHeader as normalizeHeaderForMatch } from '../column-signature.js';

/**
 * Returns the original header string from `headers` that matches `target`
 * case/whitespace-insensitively, or `undefined` when no header matches.
 */
export function findHeader(headers: string[], target: string): string | undefined {
  const normalizedTarget = normalizeHeaderForMatch(target);
  return headers.find((header) => normalizeHeaderForMatch(header) === normalizedTarget);
}

/**
 * Returns the first header from `headers` that matches any of `targets`
 * case/whitespace-insensitively, or `undefined` when none match.
 */
export function findHeaderAmong(headers: string[], targets: string[]): string | undefined {
  for (const target of targets) {
    const match = findHeader(headers, target);
    if (match !== undefined) return match;
  }
  return undefined;
}

export { normalizeHeaderForMatch };

/** Zero-pads a 2-digit date component (e.g. `3` -> `'03'`). Shared by every
 * resolver/date-format module in this directory that builds an ISO date
 * string, so a future change to date-component formatting can't silently
 * diverge between them. */
export function pad(n: number): string {
  return String(n).padStart(2, '0');
}
